import type {
	PlaceOrModifyOrdersRequest,
	PlaceOrModifyOrdersResponse,
	GetAllOrdersRequest,
	CancelOrdersRequest,
	CancelOrdersResponse,
	Holding,
	TradingAccountMargin,
	GetTradingAccountMarginRequest
} from '../proto/generated/trading-bridge'
import type { MarginFromBrokerRequest, MarginFromBrokerResponse, Order } from '../proto/generated/order'
import type { Position, PositionRequest, PositionResponse } from '../proto/generated/position'
import type { Exchange } from '../proto/generated/common'
import { Security } from '../proto/generated/security'
import {
	AuthProviderLoginUrlResponse,
	ForgetPasswordRequest,
	GetActiveSessionsResponse,
	LoginRequest,
	LoginResponse,
	LoginWithMobileRequest,
	LoginWithMobileResponse,
	OperationResponse,
	User,
	UserSignUpRequest,
	ValidateThirdPartyTokenRequest,
	VerifyMobileLoginRequest
} from '../proto/generated/identity'

export enum BackendProvider {
	TICK2TRADE = 'tick2trade',
	SHOONYA = 'shoonya'
}

/**
 * Feed kinds a provider (broker) websocket can stream on request.
 * Keys are provider-specific opaque strings (e.g. Shoonya: "NSE|22").
 */
export type ProviderWebSocketFeedKind = 'touchline' | 'depth'

/** Account-level streams that need an explicit subscribe frame after connect. */
export type ProviderWebSocketStream = 'orders' | 'positions'

/**
 * Per-mode configuration for an *extra* provider websocket that lives next to the
 * tick2trade backend websocket. `enabled` is the master switch:
 *   - tick2trade mode: false (the backend websocket already streams everything)
 *   - shoonya mode:    true  (Noren websocket for order/position/market feeds)
 */
export interface ProviderWebSocketConfig {
	enabled: boolean
	url: string
	/** Must match the `source` used at login (WEB / MOB). */
	source: string
	heartbeatIntervalMs: number
	reconnectDelayMs: number
	maxReconnectDelayMs: number
	features: {
		/** Push broker order updates into the orders store. */
		orderUpdates: boolean
		/** Push broker position updates into the positions store. */
		positionUpdates: boolean
		/** Allow touchline/depth subscriptions through this socket. */
		marketData: boolean
	}
}

export const DISABLED_PROVIDER_WEBSOCKET_CONFIG: ProviderWebSocketConfig = {
	enabled: false,
	url: '',
	source: 'WEB',
	heartbeatIntervalMs: 30000,
	reconnectDelayMs: 2000,
	maxReconnectDelayMs: 30000,
	features: {
		orderUpdates: false,
		positionUpdates: false,
		marketData: false
	}
}

export interface ProviderSecurityRef {
	exchange: Exchange
	code: string
}

/** Normalised quote fields (prices in provider units, i.e. rupees). */
export interface ProviderQuote {
	lastTradedPrice?: number
	percentChange?: number
	volume?: number
	open?: number
	high?: number
	low?: number
	close?: number
	averageTradePrice?: number
	openInterest?: number
	previousOpenInterest?: number
	totalOpenInterest?: number
	lastTradeTime?: string
	lastTradeQuantity?: number
	totalBuyQuantity?: number
	totalSellQuantity?: number
	lowerCircuit?: number
	upperCircuit?: number
	high52Week?: number
	low52Week?: number
	feedTime?: string
	tickSize?: number
	lotSize?: number
	pricePrecision?: number
	tradingSymbol?: string
}

export interface ProviderDepthLevel {
	price: number
	quantity: number
	orders?: number
}

export type ProviderWebSocketEvent =
	| { type: 'connected'; uid?: string }
	| { type: 'connect_rejected'; reason: string }
	| { type: 'heartbeat_ack'; feedTime?: string }
	| { type: 'subscription_ack'; kind: ProviderWebSocketFeedKind; key: string; security: ProviderSecurityRef | null; quote: ProviderQuote; raw: unknown }
	| { type: 'unsubscription_ack'; kind: ProviderWebSocketFeedKind; keys: string[] }
	| { type: 'stream_ack'; stream: ProviderWebSocketStream; subscribed: boolean; raw: unknown }
	| { type: 'touchline'; key: string; security: ProviderSecurityRef | null; quote: ProviderQuote; bids: ProviderDepthLevel[]; asks: ProviderDepthLevel[]; raw: unknown }
	| { type: 'depth'; key: string; security: ProviderSecurityRef | null; quote: ProviderQuote; bids: ProviderDepthLevel[]; asks: ProviderDepthLevel[]; raw: unknown }
	| { type: 'order_update'; order: Order | null; reportType?: string; raw: unknown }
	| { type: 'position_update'; position: Position | null; raw: unknown }
	| { type: 'alert'; message: string; raw: unknown }
	| { type: 'admin_message'; message: string; messageType?: string; timestamp?: string; raw: unknown }
	| { type: 'market_status'; exchange: string; status: string; marketType: string; description: string; timestamp?: string; raw: unknown }
	| { type: 'unknown'; raw: unknown }

/**
 * Wire-protocol strategy for the provider websocket. Each backend that needs an extra
 * socket implements this; the React layer stays provider-agnostic.
 * Any `build*` method returns null when the message cannot be produced yet
 * (e.g. the session token or user id is not available).
 */
export interface IProviderWebSocketProtocol {
	buildConnectMessage(): string | null
	buildHeartbeatMessage(): string | null
	buildSubscribeMessage(kind: ProviderWebSocketFeedKind, keys: string[]): string | null
	buildUnsubscribeMessage(kind: ProviderWebSocketFeedKind, keys: string[]): string | null
	/** Subscribe / unsubscribe an account stream (order updates, position updates). */
	buildSubscribeStreamMessage(stream: ProviderWebSocketStream): string | null
	buildUnsubscribeStreamMessage(stream: ProviderWebSocketStream): string | null
	/** Provider-specific subscription key for a security, or null if it cannot be derived. */
	getSubscriptionKey(security: ProviderSecurityRef): string | null
	parseMessage(raw: string): ProviderWebSocketEvent[]
}

export interface BackendConfig {
	provider: BackendProvider
	baseUrl: string
	providerUrl?: string
	wsUrl?: string
	apiKey?: string
	headers?: Record<string, string>
	providerWebSocket?: ProviderWebSocketConfig
}

export interface IBackendAdapter {
	getProvider(): BackendProvider
	getBaseUrl(): string
	getProviderUrl(): string
	getWebSocketUrl(): string
	genrateLocalSecurityMap(security: Security): void
	getProviderWebSocketConfig(): ProviderWebSocketConfig
	getProviderWebSocketProtocol(): IProviderWebSocketProtocol | null

	transformRequest<T = any>(endpoint: string, data: T, method: string): {
		url: string
		data: any
		headers?: Record<string, string>
	}

	transformResponse<T = any>(response: any, endpoint: string): T

	transformError(error: any): any

	placeOrModifyOrder(request: PlaceOrModifyOrdersRequest): Promise<PlaceOrModifyOrdersResponse>
	getOrders(request: GetAllOrdersRequest): Promise<Order[]>
	cancelOrder(request: CancelOrdersRequest): Promise<CancelOrdersResponse>
	getPositions(request: PositionRequest): Promise<PositionResponse>
	getPlacedOrder(params: any): Promise<Order>
	getHoldings(accountId: string | undefined): Promise<Holding[]>
	syncTradingAccountMargin(request: GetTradingAccountMarginRequest): Promise<TradingAccountMargin>
	getRequiredMargin(request: MarginFromBrokerRequest): Promise<MarginFromBrokerResponse>

	getLoginUrl(body: any): Promise<AuthProviderLoginUrlResponse>
	loginWithMail(body: LoginRequest): Promise<LoginResponse>
	loginWithMobile(body: LoginWithMobileRequest): Promise<LoginWithMobileResponse>
	verifyMobileLogin(body: VerifyMobileLoginRequest): Promise<LoginResponse>
	signUpWithMail(userSignUpRequest: UserSignUpRequest): Promise<LoginResponse>
	sendEmailOtp(email: string, options?: { purpose?: string }): Promise<any>
	forgetPassword(body: ForgetPasswordRequest): Promise<User>
	validateToken(body: ValidateThirdPartyTokenRequest): Promise<LoginResponse>

	logout(): void
	getActiveSessions(): Promise<GetActiveSessionsResponse>
	revokeSession(deviceId: string): Promise<OperationResponse>
}
